import { createSelector } from 'reselect';
import { benefits } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { isUnlocked } from 'features/premium/isUnlocked';
import { selectUserTier } from 'features/premium/selectors/tier';
import checkFeatureFlag from 'utils/featureFlags';
import { formatDateForItem } from 'utils/formatting';
import { CREDIT_SCORE_BANDS, formatMonthLabel, formatShortDate, } from '../utils';
import { CREDIT_REPORT_ACCOUNT_CATEGORIES, } from '../types';
const flattenFactorsByColor = (factors) => {
    if (!factors)
        return [];
    return Object.entries(factors).flatMap(([factorColor, list]) => list.map((factor) => ({ ...factor, factorColor })));
};
export const selectIsCreditScoreRegistered = (state) => state.creditScore.status === 'REGISTERED';
export const selectCreditScore = (state) => state.creditScore.data;
export const selectCreditScoreBands = createSelector([(state) => state.creditScore.bands], (bands) => bands && bands.length > 0
    ? bands.map(({ name, min, max }) => ({ label: name, min, max }))
    : CREDIT_SCORE_BANDS);
export const selectCreditScoreCooldown = (state) => state.creditScore.registerRetryCooldown;
export const selectLastSeenCreditScore = (state) => state.creditScore.lastSeenScore;
export const selectNextCreditScoreUpdate = (state) => state.creditScore.data?.nextUpdateDate;
export const selectCreditScoreDate = (state) => state.creditScore.data?.scoreDate;
export const selectShouldAnimateCreditScore = createSelector([selectCreditScore, selectLastSeenCreditScore], (data, lastSeen) => data !== undefined && data.score !== lastSeen);
export const selectCreditScoreGroupedFactors = (state) => state.enhancedFactors.data.factors;
export const selectCreditScoreFactors = createSelector([selectCreditScoreGroupedFactors], flattenFactorsByColor);
export const selectCreditScoreNextBestAction = (state) => state.enhancedFactors.data.nextBestAction;
export const selectHasEnhancedFactors = createSelector([selectCreditScoreFactors], (factors) => factors.length);
export const selectCreditReport = (state) => state.creditReport.data;
export const selectCreditReportPersonalInformation = (state) => state.creditReport.data.personalInformation;
export const selectCreditReportElectoralRoll = (state) => state.creditReport.data.electoralRoll;
export const selectCreditReportAddressLinks = (state) => state.creditReport.data.addressLinks;
export const selectCreditReportFinancialConnections = (state) => state.creditReport.data.financialConnections;
export const selectCreditReportOtherNames = (state) => state.creditReport.data.otherNames;
export const selectCreditReportInsolvencies = (state) => state.creditReport.data.insolvencies;
export const selectCreditReportJudgments = (state) => state.creditReport.data.judgments;
export const selectCreditReportCifasCases = (state) => state.creditReport.data.cifasCases;
export const selectHasCreditReport = createSelector([selectCreditReport], (data) => Object.keys(data).length > 0);
// Unknown or missing categories land in 'other' — forward-compat with new
// backend categories.
const normalizeCategory = (account) => CREDIT_REPORT_ACCOUNT_CATEGORIES.find((category) => category === account.category) ?? 'other';
const groupByCategory = (accounts, predicate) => {
    const grouped = Object.fromEntries(CREDIT_REPORT_ACCOUNT_CATEGORIES.map((category) => [
        category,
        [],
    ]));
    (accounts ?? []).forEach((account) => {
        if (predicate(account))
            grouped[normalizeCategory(account)].push(account);
    });
    return grouped;
};
export const selectCreditReportAllAccounts = (state) => state.creditReport.data.accounts ?? [];
export const selectCreditReportAccounts = createSelector([(state) => state.creditReport.data.accounts], (accounts) => groupByCategory(accounts, (account) => !account.isClosed));
export const selectCreditReportClosedAccounts = createSelector([(state) => state.creditReport.data.accounts], (accounts) => groupByCategory(accounts, (account) => account.isClosed));
export const selectCreditReportClosedAccountsCount = createSelector([(state) => state.creditReport.data.accounts], (accounts) => (accounts ?? []).filter((account) => account.isClosed).length);
const TRACKABLE_DEBT_CATEGORIES = [
    'personalLoan',
    'hirePurchase',
    'mortgage',
];
export const selectIsTrackYourMortgageEnabled = (state) => checkFeatureFlag(state.utils.featureFlags, 'track_your_mortgage');
export const selectIsCreditReportDebtTrackingUnlocked = createSelector([selectUserTier, selectIsTrackYourMortgageEnabled], (tier, isEnabled) => isEnabled && isUnlocked(tier, benefits.creditScoreReports.minTier));
/**
 * Open mortgages, personal loans and hire purchases that the backend imported
 * as net worth accounts.
 */
export const selectCreditReportTrackableDebts = createSelector([
    (state) => state.creditReport.data.accounts,
    selectIsCreditReportDebtTrackingUnlocked,
], (accounts, isUnlockedForTier) => isUnlockedForTier
    ? (accounts ?? []).filter((account) => !account.isClosed &&
        account.networthAccountId != null &&
        TRACKABLE_DEBT_CATEGORIES.includes(normalizeCategory(account)))
    : []);
export const selectUntrackedCreditReportDebts = createSelector([selectCreditReportTrackableDebts], (debts) => debts.filter((debt) => !debt.isTrackedInNetworth));
export const selectTrackedCreditReportDebts = createSelector([selectCreditReportTrackableDebts], (debts) => debts.filter((debt) => debt.isTrackedInNetworth));
/**
 * The card shows while there are untracked debts the user hasn't dismissed it
 * for. A debt imported after the last dismissal brings it back.
 */
export const selectShouldShowTrackDebtsCard = createSelector([
    selectUntrackedCreditReportDebts,
    (state) => state.deviceSettings.dismissedTrackCreditReportDebtsIds,
], (untrackedDebts, dismissedIds) => untrackedDebts.some((debt) => debt.networthAccountId != null &&
    !dismissedIds?.includes(debt.networthAccountId)));
export const selectCreditReportAccountByNetworthAccountId = (state, networthAccountId) => networthAccountId == null
    ? undefined
    : selectCreditReportAllAccounts(state).find((account) => account.networthAccountId === networthAccountId);
const OFFER_CARD_COOLDOWN_MS = 1000 * 3600 * 24 * 90; // 90 days
const isRetired = (dismissedAt, completedAt) => {
    const latest = Math.max(dismissedAt ?? 0, completedAt ?? 0);
    return latest > 0 && Date.now() - latest < OFFER_CARD_COOLDOWN_MS;
};
export const selectShouldShowAccountConsolidateDebtCard = (state, accountId) => {
    const account = selectCreditReportAccountById(state, accountId);
    if (!account ||
        account.isClosed ||
        (account.category !== 'creditCard' && account.category !== 'personalLoan')) {
        return false;
    }
    const accounts = selectCreditReportAccounts(state);
    if (accounts.creditCard.length + accounts.personalLoan.length < 2) {
        return false;
    }
    return !isRetired(state.deviceSettings.lastTimeDismissCreditReportConsolidateDebt, state.deviceSettings.monevoDebtQuoteCompletedAt);
};
export const selectCreditReportOfferCards = createSelector([
    selectCreditReportAccounts,
    (store) => store.deviceSettings.lastTimeDismissCreditReportConsolidateDebt,
    (store) => store.deviceSettings.monevoDebtQuoteCompletedAt,
    (store) => store.deviceSettings.lastTimeDismissCreditReportCreditCardOffers,
    (store) => store.deviceSettings.monevoCreditCardQuoteCompletedAt,
    (store) => selectShouldShowTrackDebtsCard(store),
], (accounts, consolidateDismissedAt, consolidateCompletedAt, offersDismissedAt, offersCompletedAt, shouldShowTrackDebts) => {
    const cardCount = accounts.creditCard.length;
    const loanCount = accounts.personalLoan.length;
    const placement = {};
    const showConsolidateDebt = cardCount >= 2 ||
        (cardCount === 1 && loanCount >= 1) ||
        (cardCount === 0 && loanCount >= 2);
    if (showConsolidateDebt &&
        !isRetired(consolidateDismissedAt, consolidateCompletedAt)) {
        const section = cardCount >= 2 ? 'creditCard' : 'personalLoan';
        placement[section] = [
            ...(placement[section] ?? []),
            'consolidateDebt',
        ];
    }
    if (cardCount <= 1 && !isRetired(offersDismissedAt, offersCompletedAt)) {
        placement.creditCard = [
            ...(placement.creditCard ?? []),
            'creditCardOffers',
        ];
    }
    if (shouldShowTrackDebts) {
        const section = [...TRACKABLE_DEBT_CATEGORIES]
            .reverse()
            .find((category) => accounts[category].length > 0);
        if (section) {
            placement[section] = [
                ...(placement[section] ?? []),
                'trackDebts',
            ];
        }
    }
    return placement;
});
export const selectCreditReportDate = (state) => state.creditReport.data.reportDate;
export const selectCreditReportAccountById = (state, accountId) => accountId == null
    ? undefined
    : selectCreditReportAllAccounts(state).find((account) => account.accountId === accountId);
export const selectSuggestedConnections = createSelector([
    (state) => state.creditReport.data.suggestedConnections,
    (state) => state.creditReport.prunedSuggestedBankIds,
], (suggestedConnections, prunedBankIds) => {
    if (!suggestedConnections || suggestedConnections.length === 0) {
        return [];
    }
    if (!prunedBankIds || prunedBankIds.length === 0) {
        return suggestedConnections;
    }
    const remaining = suggestedConnections.filter((suggestion) => suggestion.bankId == null || !prunedBankIds.includes(suggestion.bankId));
    return remaining.length > 0 ? remaining : [];
});
export const selectPendingConnectionRefetch = (state) => state.creditReport.pendingConnectionRefetch === true;
export const selectConnectionSuggestionForReportAccount = (state, tuAccountId) => {
    const connection = selectCreditReportAccountById(state, tuAccountId)?.connection;
    if (connection?.status !== 'connectable' || connection.bankId == null) {
        return undefined;
    }
    return selectSuggestedConnections(state).find((suggestion) => suggestion.bankId === connection.bankId);
};
export const selectCreditReportSearches = (state, isSoft) => isSoft
    ? state.creditReport.data.searches?.soft
    : state.creditReport.data.searches?.hard;
export const selectCreditReportSearchByReference = (state, searchReference) => {
    if (searchReference == null)
        return undefined;
    const { searches } = state.creditReport.data;
    const matches = (search) => search.searchReference === searchReference;
    return searches?.hard.find(matches) ?? searches?.soft.find(matches);
};
export const selectIsSoftCreditReportSearch = (state, searchReference) => searchReference != null &&
    !!state.creditReport.data.searches?.soft.some((search) => search.searchReference === searchReference);
export const selectIsCreditReportFetching = (state) => state.creditReport.isFetching;
export const selectCreditScoreHasRealTimeUpdates = createSelector([selectUserTier], (tier) => isUnlocked(tier, Tier.ultimate));
export const selectFullCreditAlertsUnlocked = createSelector([selectUserTier], (tier) => isUnlocked(tier, benefits.creditScoreAlerts.minTier));
export const selectShouldShowCreditScoreBellIcon = createSelector([selectUserTier, selectIsCreditScoreRegistered], (tier, isRegistered) => isUnlocked(tier, benefits.creditScore.minTier) && isRegistered);
export const selectIsCreditScoreAvailable = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.guessedHomeCountry,
], (featureFlags, homeCountry) => homeCountry === 'GB' && checkFeatureFlag(featureFlags, 'credit_score'));
export const selectIsCreditScoreReportAvailable = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.guessedHomeCountry,
], (featureFlags, homeCountry) => homeCountry === 'GB' &&
    checkFeatureFlag(featureFlags, 'credit_score_reports'));
export const selectShouldFetchCreditReportForDebtTracking = (state) => selectIsCreditReportDebtTrackingUnlocked(state) &&
    selectIsCreditScoreReportAvailable(state) &&
    selectIsCreditScoreRegistered(state) &&
    !selectIsCreditReportFetching(state);
export const selectIsCreditScoreAlertsAvailable = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.guessedHomeCountry,
], (featureFlags, homeCountry) => homeCountry === 'GB' &&
    checkFeatureFlag(featureFlags, 'credit_score_alerts'));
export const selectCreditAlerts = (state) => state.creditAlerts.alerts;
export const selectVisibleCreditAlerts = createSelector([selectCreditAlerts, selectFullCreditAlertsUnlocked], (alerts, unlocked) => (unlocked ? alerts : alerts.slice(0, 2)));
export const selectCreditAlertsSections = createSelector([selectVisibleCreditAlerts], (alerts) => {
    const entries = [];
    let activeDay = '';
    alerts.forEach((alert) => {
        const day = alert.reportedOn.substring(0, 10);
        if (day !== activeDay) {
            activeDay = day;
            entries.push({
                type: 'SECTION_HEADER',
                id: alert.reportedOn,
                sectionTitle: formatDateForItem(alert.reportedOn),
            });
        }
        entries.push(alert);
    });
    return entries;
});
export const selectCreditAlertsUnreadCount = (state) => state.creditAlerts.unreadCount;
// AB tests
export const selectShouldShowCreditScoreInFeed = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'credit_score_feed_ab'));
export const selectCreditScoreHistory = (state) => state.creditScoreHistory.entries;
export const selectShouldShowCreditScoreHistory = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'credit_score_history'));
export const selectAreAllCreditScoreFlagsEnabled = createSelector([
    selectIsCreditScoreAvailable,
    selectIsCreditScoreReportAvailable,
    selectIsCreditScoreAlertsAvailable,
    selectShouldShowCreditScoreHistory,
], (isScoreAvailable, isReportAvailable, areAlertsAvailable, showHistory) => isScoreAvailable && isReportAvailable && areAlertsAvailable && showHistory);
export const selectShouldShowSuggestedConnections = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'credit_score_suggested_connections'));
export const selectShouldShowSwitchSuggestions = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'credit_score_switch_suggestions'));
export const selectShouldShowRecurringPayments = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'credit_score_recurring_payments'));
export const selectCreditReportParams = createSelector([
    selectShouldShowSuggestedConnections,
    selectShouldShowSwitchSuggestions,
    selectShouldShowRecurringPayments,
], (includeConnectionStatus, includeSwitchSuggestion, includeRecurringPayments) => ({
    includeConnectionStatus,
    includeSwitchSuggestion,
    includeRecurringPayments,
}));
export const selectIsCreditScoreHistoryFetching = (state) => state.creditScoreHistory.isFetching;
export const selectHasCreditScoreHistory = createSelector([selectCreditScoreHistory], (entries) => entries.length > 0);
export const selectLatestCreditScoreHistoryDate = createSelector([selectCreditScoreHistory], (entries) => entries[entries.length - 1]?.date);
export const selectCreditScoreHistoryView = createSelector([selectCreditScoreHistory], (entries) => {
    let groupCount = 0;
    let prevGroupLabel;
    const dataPoints = entries.map(({ date, value }) => {
        const groupLabel = formatMonthLabel(date);
        if (groupLabel !== prevGroupLabel) {
            groupCount += 1;
            prevGroupLabel = groupLabel;
        }
        return { label: formatShortDate(date), value, groupLabel };
    });
    return {
        dataPoints,
        factorsByIndex: entries.map(({ factors }) => flattenFactorsByColor(factors)),
        groupCount,
    };
});
